import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Link,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  OpenInNew,
  AccountBalance,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface StripeAccountStatus {
  hasAccount: boolean;
  onboardingComplete: boolean;
  readyForPayouts: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  accountId?: string;
}

const ProviderStripeConnect: React.FC = () => {
  const [accountStatus, setAccountStatus] = useState<StripeAccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchAccountStatus();
  }, []);

  const fetchAccountStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/stripe-connect/account-status');
      if (response.success && response.data) {
        setAccountStatus(response.data as StripeAccountStatus);
      } else {
        setError('Failed to load account status');
      }
    } catch (err: any) {
      console.error('Error fetching account status:', err);
      setError(err.response?.data?.error || 'Failed to load account status');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await apiService.post('/stripe-connect/create-account');
      if (response.success) {
        setSuccess('Stripe Connect account created! Please complete the onboarding process.');
        // Wait a moment for the database to update
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchAccountStatus();
      } else {
        setError(response.error || response.message || 'Failed to create account');
      }
    } catch (err: any) {
      console.error('Error creating account:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create account';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      
      // First, ensure account status is fresh
      await fetchAccountStatus();
      
      // Check if account exists
      if (!accountStatus?.hasAccount) {
        setError('Please create a Stripe Connect account first.');
        setActionLoading(false);
        return;
      }
      
      const response = await apiService.post('/stripe-connect/onboarding-link');
      if (response.success && response.data) {
        // Redirect to Stripe onboarding
        const onboardingUrl = (response.data as any).url;
        if (onboardingUrl) {
          window.location.href = onboardingUrl;
        } else {
          setError('Onboarding URL not received from server');
        }
      } else {
        setError(response.error || response.message || 'Failed to generate onboarding link');
      }
    } catch (err: any) {
      console.error('Error generating onboarding link:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to generate onboarding link';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccessDashboard = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      const response = await apiService.post('/stripe-connect/login-link');
      if (response.success && response.data) {
        // Open Stripe dashboard in new tab
        window.open((response.data as any).url, '_blank');
      } else {
        setError(response.error || 'Failed to generate login link');
      }
    } catch (err: any) {
      console.error('Error generating login link:', err);
      setError(err.response?.data?.error || 'Failed to generate login link');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = () => {
    if (!accountStatus) return null;

    if (!accountStatus.hasAccount) {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Not Set Up"
          color="error"
          size="small"
        />
      );
    }

    if (accountStatus.readyForPayouts) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Ready for Payouts"
          color="success"
          size="small"
        />
      );
    }

    // If onboardingComplete is true but readyForPayouts is false, setup is incomplete
    // (e.g., waiting for Stripe to enable payouts, or additional verification needed)
    if (accountStatus.onboardingComplete && !accountStatus.readyForPayouts) {
      return (
        <Chip
          icon={<Warning />}
          label="Setup Incomplete"
          color="warning"
          size="small"
        />
      );
    }

    return (
      <Chip
        icon={<Warning />}
        label="Onboarding Required"
        color="warning"
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalance color="primary" />
          <Typography variant="h6">Stripe Account Setup</Typography>
        </Box>
        {getStatusChip()}
      </Box>

      <Divider sx={{ my: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!accountStatus?.hasAccount ? (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Set up your Stripe Connect account to receive payouts from customer payments. 
            This is a secure process managed by Stripe, and you'll need to provide some 
            business and banking information.
          </Typography>
          <Button
            variant="contained"
            onClick={handleCreateAccount}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <AccountBalance />}
          >
            {actionLoading ? 'Creating Account...' : 'Create Stripe Account'}
          </Button>
        </Box>
      ) : !accountStatus.onboardingComplete ? (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your Stripe Connect account has been created, but you need to complete the 
            onboarding process to start receiving payouts. You'll need to provide:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Business information</li>
            <li>Tax identification number</li>
            <li>Banking details</li>
          </Box>
          <Button
            variant="contained"
            onClick={handleStartOnboarding}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <OpenInNew />}
          >
            {actionLoading ? 'Loading...' : 'Complete Onboarding'}
          </Button>
        </Box>
      ) : accountStatus.readyForPayouts ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            Your Stripe Connect account is fully set up and ready to receive payouts!
          </Alert>
          <Typography variant="body2" color="text.secondary" paragraph>
            Account ID: <strong>{accountStatus.accountId}</strong>
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={handleAccessDashboard}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : <OpenInNew />}
            >
              {actionLoading ? 'Loading...' : 'Access Stripe Dashboard'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleStartOnboarding}
              disabled={actionLoading}
            >
              Update Account Details
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            You can update your account information or view payout history in your Stripe Express dashboard.
          </Typography>
        </Box>
      ) : (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your account setup is incomplete. Please complete the onboarding process to receive payouts.
          </Alert>
          <Button
            variant="contained"
            onClick={handleStartOnboarding}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <OpenInNew />}
          >
            {actionLoading ? 'Loading...' : 'Complete Setup'}
          </Button>
        </Box>
      )}

      <Box mt={3}>
        <Typography variant="caption" color="text.secondary">
          Powered by{' '}
          <Link href="https://stripe.com" target="_blank" rel="noopener noreferrer">
            Stripe Connect
          </Link>
          {' '}for secure payment processing and payouts.
        </Typography>
      </Box>
    </Paper>
  );
};

export default ProviderStripeConnect;
